import type { PipeTransform } from '@angular/core';
import { inject, InjectionToken, Pipe } from '@angular/core';

import type { Observable } from 'rxjs';
import { BehaviorSubject, switchMap } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';

const socialMediaMapping = [
  { url: 'about.me', type: 'aboutme' },
  { url: 'academia.edu', type: 'academiaedu' },
  { url: 'thisisamplify.com', type: 'amplify' },
  { url: 'angel.co', type: 'angellist' },
  { url: 'bandcamp.com', type: 'bandcamp' },
  { url: 'bebo.com', type: 'bebo' },
  { url: 'behance.net', type: 'behance' },
  { url: 'bitbucket.org', type: 'bitbucket' },
  { url: 'blip.fm', type: 'blipfm' },
  { url: 'blogger.com', type: 'blogger' },
  { url: 'crunchbase.com', type: 'crunchbase' },
  { url: 'dandyid.org', type: 'dandyid' },
  { url: 'delicious.com', type: 'delicious' },
  { url: 'deviantart.com', type: 'deviantart' },
  { url: 'digg.com', type: 'digg' },
  { url: 'diigo.com', type: 'diigo' },
  { url: 'disqus.com', type: 'disqus' },
  { url: 'doyoubuzz.com', type: 'doyoubuzz' },
  { url: 'dribbble.com', type: 'dribbble' },
  { url: 'econsultancy.com', type: 'econsultancy' },
  { url: 'flavors.me', type: 'flavorsme' },
  { url: 'flickr.com', type: 'flickr' },
  { url: 'foursquare.com', type: 'foursquare' },
  { url: 'friendster.com', type: 'friendster' },
  { url: 'getsatisfaction.com', type: 'getsatisfaction' },
  { url: 'github.com', type: 'github' },
  { url: 'goodreads.com', type: 'goodreads' },
  { url: 'plus.google.com', type: 'googleplus' },
  { url: 'gravatar.com', type: 'gravatar' },
  { url: 'news.ycombinator.com', type: 'hackernews' },
  { url: 'hi5.com', type: 'hi5' },
  { url: 'hypem.com', type: 'hypemachine' },
  { url: 'imdb.com', type: 'imdb' },
  { url: 'intensedebate.com', type: 'intensedebate' },
  { url: 'instagram.com', type: 'instagram' },
  { url: 'klout.com', type: 'klout' },
  { url: 'lanyrd.com', type: 'lanyrd' },
  { url: 'last.fm', type: 'lastfm' },
  { url: 'linkedin.com', type: 'linkedin' },
  { url: 'meetup.com', type: 'meetup' },
  { url: 'mixcloud.com', type: 'mixcloud' },
  { url: 'pandora.com', type: 'pandora' },
  { url: 'picasa.google.com', type: 'picasa' },
  { url: 'pinboard.in', type: 'pinboard' },
  { url: 'pinterest.com', type: 'pinterest' },
  { url: 'plancast.com', type: 'plancast' },
  { url: 'plaxo.com', type: 'plaxo' },
  { url: 'plurk.com', type: 'plurk' },
  { url: 'quora.com', type: 'quora' },
  { url: 'reddit.com', type: 'reddit' },
  { url: 'reverbnation.com', type: 'reverbnation' },
  { url: 'scribd.com', type: 'scribd' },
  { url: 'shelfari.com', type: 'shelfari' },
  { url: 'skype.com', type: 'skype' },
  { url: 'slideshare.net', type: 'slideshare' },
  { url: 'smugmug.com', type: 'smugmug' },
  { url: 'soundcloud.com', type: 'soundcloud' },
  { url: 'stackexchange.com', type: 'stackexchange' },
  { url: 'stackoverflow.com', type: 'stackoverflow' },
  { url: 'stumbleupon.com', type: 'stumbleupon' },
  { url: 'tagged.com', type: 'tagged' },
  { url: 'tripit.com', type: 'tripit' },
  { url: 'tumblr.com', type: 'tumblr' },
  { url: 'twitter.com', type: 'twitter' },
  { url: 'typepad.com', type: 'typepad' },
  { url: 'vimeo.com', type: 'vimeo' },
  { url: 'vk.com', type: 'vk' },
  { url: 'wordpress.com', type: 'wordpress' },
  { url: 'x.com', type: 'x' },
  { url: 'xing.com', type: 'xing' },
  { url: 'yelp.com', type: 'yelp' },
  { url: 'youtube.com', type: 'youtube' },
  { url: 'facebook', type: 'facebook' },
  { url: 'fb.com', type: 'facebook' },
];

export const IconProvider = new InjectionToken<(name: string) => Promise<{ default: string }>>(
  'IconProvider',
);

@Pipe({
  name: 'rtSocialMedia',
  standalone: true,
})
export class SocialMediaPipe implements PipeTransform {
  private url$ = new BehaviorSubject<string>('');
  private icon$ = this.url$.pipe(
    distinctUntilChanged(),
    map(url => {
      const typeItem = this.getTypeItem(url);
      return typeItem ? typeItem.type : 'unknown';
    }),
    switchMap(type => this.iconProvider(type)),
    map(iconModule => iconModule.default),
    shareReplay(),
  );

  private iconProvider = inject(IconProvider);

  public transform(url: string): Observable<string> {
    this.url$.next(url);
    return this.icon$;
  }

  private getTypeItem(url: string) {
    if (!url) return false;
    return socialMediaMapping.find(sm => url.indexOf(sm.url) !== -1);
  }
}
